import { formatDistanceToNow, format } from 'date-fns'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  ExternalLink,
  Settings,
  User,
  Users,
  Activity,
  Shield,
  ShoppingCart,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import {
  type Site,
  type LoginLog,
  parseSiteSettings,
  StatusBadge,
  HealthBadge,
  StatCard,
} from './shared'

interface OverviewTabProps {
  site: Site
  logs: LoginLog[]
}

export function OverviewTab({ site, logs }: OverviewTabProps) {
  const settings = parseSiteSettings(site.settings)
  const successfulLogins = logs.filter(l => l.success).length
  const failedLogins = logs.filter(l => !l.success).length
  const hasWooCommerce = site.woocommerce_active === 1

  return (
    <div className="space-y-4">
      {/* Status & Health */}
      <div className="flex flex-wrap gap-2">
        <StatusBadge status={site.status} />
        <HealthBadge lastActivityAt={site.last_activity_at} />
        {settings.disable_password_login && (
          <Badge variant="outline" className="flex items-center w-fit">
            <Shield className="h-3 w-3 mr-1" />
            OAuth Only
          </Badge>
        )}
        {hasWooCommerce && (
          <Badge variant="outline" className="flex items-center w-fit border-purple-500 text-purple-600 dark:text-purple-400">
            <ShoppingCart className="h-3 w-3 mr-1" />
            WooCommerce{site.woocommerce_version && ` ${site.woocommerce_version}`}
          </Badge>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Last Login"
          value={site.last_login_at
            ? formatDistanceToNow(new Date(site.last_login_at), { addSuffix: true })
            : 'Never'
          }
          icon={<User className="h-4 w-4" />}
        />
        <StatCard
          label="Last Activity"
          value={site.last_activity_at
            ? formatDistanceToNow(new Date(site.last_activity_at), { addSuffix: true })
            : 'Never'
          }
          icon={<Activity className="h-4 w-4" />}
        />
        <StatCard
          label="Total Users"
          value={site.user_count_total ?? '-'}
          icon={<Users className="h-4 w-4" />}
        />
        <StatCard
          label="Admins"
          value={site.user_count_admin ?? '-'}
          icon={<Shield className="h-4 w-4 text-amber-500" />}
        />
      </div>

      {/* Login Stats (smaller row) */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center justify-between p-2 rounded border bg-card text-sm">
          <span className="text-muted-foreground flex items-center gap-1">
            <CheckCircle className="h-3 w-3 text-green-500" />
            Successful
          </span>
          <span className="font-medium">{successfulLogins}</span>
        </div>
        <div className="flex items-center justify-between p-2 rounded border bg-card text-sm">
          <span className="text-muted-foreground flex items-center gap-1">
            <XCircle className="h-3 w-3 text-red-500" />
            Failed
          </span>
          <span className="font-medium">{failedLogins}</span>
        </div>
      </div>

      {/* Dates */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Registered</span>
          <span>{site.created_at ? format(new Date(site.created_at), 'PPP') : '-'}</span>
        </div>
        {site.approved_at && (
          <div className="flex justify-between py-2 border-b">
            <span className="text-muted-foreground">Approved</span>
            <span>{format(new Date(site.approved_at), 'PPP')}</span>
          </div>
        )}
        {site.site_info_updated_at && (
          <div className="flex justify-between py-2 border-b">
            <span className="text-muted-foreground">Last Scan</span>
            <span>{formatDistanceToNow(new Date(site.site_info_updated_at), { addSuffix: true })}</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" asChild>
          <a href={`https://${site.domain}`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4 mr-2" />
            Visit Site
          </a>
        </Button>
        <Button variant="outline" className="flex-1" asChild>
          <a href={`https://${site.domain}/wp-admin/`} target="_blank" rel="noopener noreferrer">
            <Settings className="h-4 w-4 mr-2" />
            WP Admin
          </a>
        </Button>
      </div>
    </div>
  )
}
